import * as XLSX from 'xlsx';

// Section and group (#68) are optional columns: older three-column sheets
// still parse, and blank cells mean "do not assign".
export const TEMPLATE_HEADERS = ['الاسم الأول', 'اسم العائلة', 'رقم الجوال', 'القسم', 'المجموعة'] as const;

export interface RawRow {
  firstNameAr: string;
  lastNameAr: string;
  phone: string;
  sectionName?: string;
  groupName?: string;
}

export type RowStatus =
  | 'valid'
  // #71: no phone (e.g. a child). Imported without a sign-in identity; they
  // appear in rosters and exams and can log in once a phone is added.
  | 'no-phone'
  | 'invalid-phone'
  | 'missing-name'
  | 'duplicate-in-file'
  | 'already-registered';

export interface ParsedRow {
  rowNumber: number;
  firstNameAr: string;
  lastNameAr: string;
  phone: string;
  firstNameEn: string;
  lastNameEn: string;
  /** #68: names as typed in the sheet; '' when blank. */
  sectionName: string;
  groupName: string;
  status: RowStatus;
}

/** Statuses that are sent to createManagedUsersBatch. */
export function isImportable(status: RowStatus): boolean {
  return status === 'valid' || status === 'no-phone';
}

export function normalizePhone(raw: string): string {
  return String(raw || '').replace(/\D/g, '').replace(/^966/, '0');
}

export function parseWorkbook(fileBuffer: ArrayBuffer): RawRow[] {
  const wb = XLSX.read(fileBuffer, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  const [H_FIRST, H_LAST, H_PHONE, H_SECTION, H_GROUP] = TEMPLATE_HEADERS;
  return json.map((r) => ({
    firstNameAr: String(r[H_FIRST] ?? '').trim(),
    lastNameAr: String(r[H_LAST] ?? '').trim(),
    phone: String(r[H_PHONE] ?? '').trim(),
    sectionName: String(r[H_SECTION] ?? '').trim(),
    groupName: String(r[H_GROUP] ?? '').trim(),
  }));
}

export function validateRows(raw: RawRow[]): ParsedRow[] {
  const seen = new Set<string>();
  // Phone-less rows have no unique key, so the same full name twice in one
  // file is treated as a duplicate rather than creating two accounts.
  const seenPhonelessNames = new Set<string>();
  return raw.map((r, i) => {
    const firstNameAr = String(r.firstNameAr || '').trim();
    const lastNameAr = String(r.lastNameAr || '').trim();
    const hasPhoneCell = String(r.phone ?? '').trim() !== '';
    const phone = hasPhoneCell ? normalizePhone(r.phone) : '';
    const nameKey = `${firstNameAr} ${lastNameAr}`.replace(/\s+/g, ' ');

    let status: RowStatus = 'valid';
    if (firstNameAr.length < 2 || lastNameAr.length < 2) {
      status = 'missing-name';
    } else if (!hasPhoneCell) {
      status = seenPhonelessNames.has(nameKey) ? 'duplicate-in-file' : 'no-phone';
    } else if (!/^05\d{8}$/.test(phone)) {
      status = 'invalid-phone';
    } else if (seen.has(phone)) {
      status = 'duplicate-in-file';
    }

    if (status === 'valid') seen.add(phone);
    if (status === 'no-phone') seenPhonelessNames.add(nameKey);

    return {
      rowNumber: i + 1,
      firstNameAr,
      lastNameAr,
      phone,
      firstNameEn: '',
      lastNameEn: '',
      sectionName: String(r.sectionName ?? '').trim(),
      groupName: String(r.groupName ?? '').trim(),
      status,
    };
  });
}

export function buildTemplateBlob(): Blob {
  const ws = XLSX.utils.aoa_to_sheet([[...TEMPLATE_HEADERS]]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'customers');
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
